import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import ChorusSettingsCard from '@/components/ChorusSettingsCard';
import PennylaneSettingsCard from '@/components/PennylaneSettingsCard';
import GoogleSettingsCard from '@/components/GoogleSettingsCard';
import MailboxesSettingsCard from '@/components/MailboxesSettingsCard';
import ProspectsSettingsCard from '@/components/ProspectsSettingsCard';
import CompanyStampSettingsCard from '@/components/CompanyStampSettingsCard';
import TarifQuestionsSettingsCard from '@/components/TarifQuestionsSettingsCard';
import ProspectTestEmailSettingsCard from '@/components/ProspectTestEmailSettingsCard';
import CeeObligesSettingsCard from '@/components/CeeObligesSettingsCard';
import PageErrorBoundary from '@/components/PageErrorBoundary';

export default function Parametres() {
  const [searchParams] = useSearchParams();
  const [googleStatus, setGoogleStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Paramètres - Reno Resolve';
    fetchGoogleStatus();

    // Toasts sur retour OAuth Google
    if (searchParams.get('google_connected') === 'true') {
      toast.success('Google connecté avec succès !');
    }
    if (searchParams.get('google_error')) {
      toast.error(`Erreur Google: ${searchParams.get('google_error')}`);
    }
  }, [searchParams]);

  const fetchGoogleStatus = async () => {
    try {
      const response = await api.get('/google/status');
      setGoogleStatus(response.data);
    } catch (error) {
      console.error('Erreur statut Google:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnectGoogle = async () => {
    try {
      const response = await api.get('/google/connect');
      window.location.href = response.data.authorization_url;
    } catch (error) {
      console.error('Erreur connexion Google:', error);
      toast.error(error?.response?.data?.detail || 'Erreur lors de la connexion Google');
    }
  };

  const handleDisconnectGoogle = async () => {
    if (!window.confirm('Voulez-vous vraiment déconnecter Google ?')) return;
    try {
      await api.post('/google/disconnect');
      toast.success('Google déconnecté');
      setGoogleStatus({ connected: false });
    } catch (error) {
      console.error('Erreur déconnexion:', error);
      toast.error('Erreur lors de la déconnexion');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full" data-testid="parametres-loading">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="parametres-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Paramètres
        </h1>
        <p className="text-slate-600 mt-1">
          Intégrations Google Drive &amp; Sheets, Chorus Pro, Pennylane, Obligés CEE
        </p>
      </div>

      {/* Google Drive & Sheets — connexion + identifiants + migration unifiés */}
      <PageErrorBoundary>
        <GoogleSettingsCard
          googleStatus={googleStatus}
          onConnect={handleConnectGoogle}
          onDisconnect={handleDisconnectGoogle}
        />
      </PageErrorBoundary>

      {/* Boîtes mail à scanner (IONOS + Gmail App Password + autres) */}
      <PageErrorBoundary>
        <MailboxesSettingsCard />
      </PageErrorBoundary>

      {/* Chorus Pro (B2G) */}
      <PageErrorBoundary>
        <ChorusSettingsCard />
      </PageErrorBoundary>

      {/* Pennylane (Comptabilité) */}
      <PageErrorBoundary>
        <PennylaneSettingsCard />
      </PageErrorBoundary>

      {/* Obligés CEE + Mention RAI */}
      <PageErrorBoundary>
        <CeeObligesSettingsCard />
      </PageErrorBoundary>

      {/* Prospections — Modèles PDF (plaquettes par catégorie + convention) */}
      <PageErrorBoundary>
        <ProspectsSettingsCard />
      </PageErrorBoundary>

      {/* Emails prospects — mode test / réel (à vérifier avant de scanner en prod) */}
      <PageErrorBoundary>
        <ProspectTestEmailSettingsCard />
      </PageErrorBoundary>

      {/* Questions tarifaires prospects sous-traitants (bordereau d'onboarding) */}
      <PageErrorBoundary>
        <TarifQuestionsSettingsCard />
      </PageErrorBoundary>

      {/* Tampon d'entreprise — apposé sur les PV de réception */}
      <PageErrorBoundary>
        <CompanyStampSettingsCard />
      </PageErrorBoundary>
    </div>
  );
}
